import { FormCard } from "@atlas/core";
import { getCheckoutCartSectionHeadings, type IOrderedCartEntry } from "@utils";
import type { ReactNode } from "react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";
import { CheckoutCartEntryBlock } from "./CheckoutCartEntryBlock";

export interface ICheckoutCartOrderedFormLineProps<
  TFieldValues extends FieldValues = FieldValues,
> {
  orderedCartEntries: IOrderedCartEntry[];
  orderedIndex: number;
  entry: IOrderedCartEntry;
  control: Control<TFieldValues>;
  nestedAddonsTitle: ReactNode;
  otherAddonsTitle: ReactNode;
  formCardContentClassName?: string;
  renderFormCardContent: () => ReactNode;
  isLoading?: boolean;
}

/**
 * One ordered cart line: section headings + rail + FormCard row bound to
 * `lineIds.{rowIndex}` (shared by full checkout and header cart popover).
 */
export function CheckoutCartOrderedFormLine<
  TFieldValues extends FieldValues = FieldValues,
>({
  orderedCartEntries,
  orderedIndex,
  entry,
  control,
  nestedAddonsTitle,
  otherAddonsTitle,
  formCardContentClassName,
  renderFormCardContent,
  isLoading,
}: ICheckoutCartOrderedFormLineProps<TFieldValues>) {
  const { rowIndex } = entry;
  const { showNestedAddonsHeading, showOtherAddonsHeading } =
    getCheckoutCartSectionHeadings(orderedCartEntries, orderedIndex);

  return (
    <CheckoutCartEntryBlock
      entry={entry}
      showNestedAddonsHeading={showNestedAddonsHeading}
      showOtherAddonsHeading={showOtherAddonsHeading}
      nestedAddonsTitle={nestedAddonsTitle}
      otherAddonsTitle={otherAddonsTitle}>
      <FormCard
        isLoading={isLoading}
        control={control}
        name={`lineIds.${rowIndex}` as FieldPath<TFieldValues>}
        contentClassName={formCardContentClassName}
        renderContent={renderFormCardContent}
      />
    </CheckoutCartEntryBlock>
  );
}
